import { useAppQuery } from '@/hooks/useAppQuery';
import TradingProfileSelect from '@/components/UI/TradingProfileSelect';
import { useRef, useState } from 'react';
import { Button, Card, FormInput, FormCheckbox } from '@/components/UI';
import { trpc } from '@/api/trpc';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { showToast } from '@/components/Layout/Toast';
import FuturesConfigurationFields from './FuturesConfigurationFields';
import { emptyFuturesConfiguration, parseFuturesConfiguration } from './futuresConfigurationDraft';
import type { FuturesBacktestSelection } from '@einhaus/project314-types';

interface Request {
    strategyId: number;
    tradingProfileId: number;
    entityType: 'FuturesProduct';
    entityId: number;
    fixedWindow: { startDateTime: string; endDateTime: string };
    futuresConfiguration: FuturesBacktestSelection;
    bypassCache: boolean;
    priority: number;
}
export default function FuturesBacktestCreation({
    strategyId,
    canManage,
    usesEtfSignals = false
}: {
    strategyId: number;
    canManage: boolean;
    usesEtfSignals?: boolean;
}) {
    const selectionRevision = useRef(0);
    const [draft, setDraft] = useState(emptyFuturesConfiguration);
    const [bypassCache, setBypassCache] = useState(false);
    const queue = useAppQuery(
        {
            ...trpc.backtest.getStrategyQueueStatus.queryOptions({ strategyId, entityType: null, strategyVersionKeyMode: 'current' }),
            refetchInterval: 15000
        },
        { fallbackErrorMessage: 'Failed to load futures queue status' }
    );
    const [profileId, setProfileId] = useState<number | null>(null);
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');
    const [preview, setPreview] = useState<{
        request: Request;
        count: number;
        configurationKey: string;
        reused: number[];
        pending: number[];
    } | null>(null);
    const previewMutation = useTrpcMutation(trpc.backtest.previewPopulateForStrategy.mutationOptions(), {
        fallbackErrorMessage: 'Futures preview failed'
    });
    const submitMutation = useTrpcMutation(trpc.backtest.triggerPopulateForStrategy.mutationOptions(), {
        fallbackErrorMessage: 'Futures submission failed'
    });
    const clear = () => {
        selectionRevision.current++;
        setPreview(null);
    };
    return (
        <Card
            title="Create futures backtest"
            subtitle="Historical research only. One continuous account, up to three years including warmup and 500,000 entries. Positions close before session ends and signal rolls."
        >
            <div className="flex flex-col gap-4">
                <FuturesConfigurationFields
                    strategyIds={usesEtfSignals ? [strategyId] : []}
                    value={draft}
                    onChange={(value) => {
                        setDraft(value);
                        clear();
                    }}
                />
                <TradingProfileSelect
                    label="Research profile"
                    value={profileId}
                    onChange={(value) => {
                        setProfileId(value);
                        clear();
                    }}
                    filter={(profile) =>
                        profile.allowedUnderlyingEntityTypes.length === 1 &&
                        profile.allowedUnderlyingEntityTypes[0] === (usesEtfSignals ? 'Asset' : 'FuturesProduct')
                    }
                />
                <p className="text-sm text-zinc-400">
                    Use a {usesEtfSignals ? 'stock' : 'product'} research profile without FOMC filtering, trade filters or cash
                    replenishment.
                </p>
                <div className="grid gap-3 md:grid-cols-2">
                    <FormInput
                        label="First decision (Eastern)"
                        placeholder="2020-01-06 00:00:00"
                        value={start}
                        onChange={(event) => {
                            setStart(event.target.value);
                            clear();
                        }}
                    />
                    <FormInput
                        label="End, exclusive (Eastern)"
                        placeholder="2022-01-01 00:00:00"
                        value={end}
                        onChange={(event) => {
                            setEnd(event.target.value);
                            clear();
                        }}
                    />
                </div>
                <FormCheckbox
                    label="Recompute completed results"
                    checked={bypassCache}
                    onChange={(event) => {
                        setBypassCache(event.target.checked);
                        clear();
                    }}
                />
                <Button
                    disabled={!canManage || previewMutation.isPending || submitMutation.isPending}
                    onClick={async () => {
                        try {
                            const revision = selectionRevision.current;
                            if (!profileId) throw new Error('Select an explicit research profile');
                            const futuresConfiguration = parseFuturesConfiguration(draft);
                            if (usesEtfSignals && !futuresConfiguration.signalRouteId) throw new Error('Select a saved ETF futures route');
                            const request: Request = {
                                strategyId,
                                tradingProfileId: profileId,
                                entityType: 'FuturesProduct',
                                entityId: futuresConfiguration.productId,
                                fixedWindow: { startDateTime: start, endDateTime: end },
                                futuresConfiguration,
                                bypassCache,
                                priority: 5
                            };
                            const result = await previewMutation.mutateAsync(request);
                            if (!result.success || !result.plan?.futuresConfigurationKey)
                                throw new Error(result.errorMessages.join(', ') || 'Missing futures preview');
                            if (revision !== selectionRevision.current) return;
                            setPreview({
                                request,
                                count: result.estimatedJobCount,
                                configurationKey: result.plan.futuresConfigurationKey,
                                reused: result.plan.reusedBacktestIds,
                                pending: result.plan.pendingJobIds
                            });
                        } catch (error) {
                            showToast(error instanceof Error ? error.message : 'Preview failed', 'error');
                        }
                    }}
                >
                    Preview backtest
                </Button>
                {preview && (
                    <div className="rounded border border-zinc-700 p-3 text-sm text-zinc-300">
                        <p>
                            {preview.request.fixedWindow.startDateTime} to {preview.request.fixedWindow.endDateTime} Eastern.{' '}
                            {preview.count} new jobs, {preview.reused.length} reusable results, {preview.pending.length} already queued.
                        </p>
                        <Button
                            disabled={!canManage || submitMutation.isPending || preview.count === 0}
                            onClick={async () => {
                                try {
                                    const result = await submitMutation.mutateAsync({
                                        ...preview.request,
                                        expectedFuturesConfigurationKey: preview.configurationKey,
                                        expectedJobCount: preview.count,
                                        acknowledgeLargeBatch: false
                                    });
                                    if (!result.success) return;
                                    showToast(`Queued ${result.submittedJobs} futures backtest jobs`, 'success');
                                    clear();
                                    void queue.refetch();
                                } catch {
                                    // useTrpcMutation displays the server or transport error.
                                }
                            }}
                        >
                            Confirm and queue
                        </Button>
                    </div>
                )}
                {queue.data?.recentJobs.length ? (
                    <div className="text-sm text-zinc-400" aria-label="Recent futures jobs">
                        <p>Recent jobs for the current strategy version</p>
                        <ul>
                            {queue.data.recentJobs.slice(0, 5).map((job) => (
                                <li key={job.id}>
                                    #{job.id}: {job.status}
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : null}
            </div>
        </Card>
    );
}
